/* Consolidate only duplicate cart rows caused by a known Studio operation tag.
 * No add, clear, property change or variant-ID update is used. Every transfer
 * keeps an existing row and conserves the quantity of the compatible group.
 */
(() => {
  'use strict';
  if (window.FabrikoneCartLines) return;
  const OP = '_fabrikone_studio_op';
  const STUDIO_KEY = 'fabrikone-studyom-cart-journal-v2';
  const JOURNAL_KEY = 'fabrikone-cart-lines-journal-v1';
  const LOCK = 'fabrikone-cart-lines-transfer-v1';
  let queue = Promise.resolve(), memoryJournal = null;
  const validOperation = value => typeof value === 'string' && /^studio-[\w-]{8,100}$/.test(value);
  const fail = (reason, message = 'Sepet satırları doğrulanamadı. Mevcut sepetini kontrol ederek tekrar deneyebilirsin.') => {
    throw Object.assign(new Error(message), { code: 'cart_lines_pending', reason });
  };
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  function ordered(value) {
    if (Array.isArray(value)) return value.map(ordered);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, ordered(value[key])]));
    return value;
  }
  const encode = value => JSON.stringify(ordered(value));
  const operation = item => validOperation(item.properties?.[OP]) ? item.properties[OP] : '';
  function actualProperties(item, normalized = false) {
    return Object.entries(item.properties || {}).filter(([key]) => !normalized || key !== OP || !operation(item)).sort(([a], [b]) => a.localeCompare(b));
  }
  function plan(item) {
    return String(item.selling_plan_allocation?.selling_plan?.id ?? item.selling_plan ?? '');
  }
  function discountDescriptor(value) {
    if (Array.isArray(value)) return value.map(discountDescriptor);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'amount' && key !== 'total_allocated_amount').map(([key, entry]) => [key, discountDescriptor(entry)]));
    return value;
  }
  function orderDiscount(item, cart) {
    const discounts = item.discounts, applications = cart?.cart_level_discount_applications;
    if (!Array.isArray(discounts) || !discounts.length || !Array.isArray(applications) || !applications.length || !Array.isArray(item.line_level_discount_allocations) || item.line_level_discount_allocations.length || Number(item.line_level_total_discount || 0) !== 0) return false;
    if (item.price !== item.original_price || item.final_price !== item.original_price || !integer(item.original_price)) return false;
    return discounts.every(discount => integer(discount.amount) && typeof discount.title === 'string' && discount.title && applications.some(application => application.title === discount.title && application.target_selection === 'all' && application.target_type === 'line_item' && application.value_type === 'percentage' && Number(application.value) > 0 && Number(application.value) <= 100));
  }
  function prices(item, cart, allocationStable = false) {
    const apportioned = allocationStable && orderDiscount(item, cart);
    return {
      price: item.price ?? null,
      original: item.original_price ?? item.price ?? null,
      final: item.final_price ?? item.discounted_price ?? item.price ?? null,
      discounted: apportioned ? 'order-level-apportionment' : item.discounted_price ?? item.final_price ?? item.price ?? null,
      unit: item.unit_price ?? null,
      measurement: item.unit_price_measurement ?? null,
      discounts: apportioned ? discountDescriptor(item.discounts) : item.discounts ?? [],
      allocations: apportioned ? discountDescriptor(item.discount_allocations ?? []) : item.discount_allocations ?? [],
      lineAllocations: item.line_level_discount_allocations ?? [],
      totalDiscount: apportioned ? 'order-level-apportionment' : item.total_discount ?? null,
      lineTotalDiscount: item.line_level_total_discount ?? null,
      sellingPlan: item.selling_plan_allocation ?? null
    };
  }
  function identity(item, normalized = false, cart, allocationStable = false) {
    return encode([String(item.variant_id ?? item.id), actualProperties(item, normalized), plan(item), prices(item, cart, allocationStable), item.variant_options ?? null, item.options_with_values ?? null, item.variant_title ?? null]);
  }
  function snapshot(cart, allocationStable = false) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length > 500) fail('invalid_cart');
    const keys = new Set();
    const rows = cart.items.map(item => {
      const id = String(item.variant_id ?? item.id), key = String(item.key || '');
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || !key.startsWith(id + ':') || key.length > 250 || keys.has(key) || !integer(item.quantity) || item.quantity < 1) fail('invalid_cart');
      if (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties))) fail('invalid_properties');
      keys.add(key);
      return { key, quantity: item.quantity, identity: identity(item, false, cart, allocationStable) };
    }).sort((a, b) => a.key.localeCompare(b.key));
    const totals = {};
    for (const name of ['item_count', 'total_price', 'items_subtotal_price', 'original_total_price', 'total_discount', 'total_weight']) {
      if (cart[name] !== undefined) { if (!integer(cart[name])) fail('invalid_cart'); totals[name] = cart[name]; }
    }
    return { token: String(cart.token || ''), currency: cart.currency ?? null, note: cart.note ?? null, attributes: cart.attributes ?? {}, discountApplications: allocationStable ? discountDescriptor(cart.cart_level_discount_applications ?? []) : cart.cart_level_discount_applications ?? [], totals, rows };
  }
  const fingerprint = cart => encode(snapshot(cart));
  const coreFingerprint = cart => encode(snapshot(cart, true));
  function samePostState(cart, expectedBody) {
    let expected;
    try { expected = JSON.parse(expectedBody); } catch (_) { fail('cleanup_journal_invalid'); }
    const withoutKeys = state => {
      if (!state || !Array.isArray(state.rows)) fail('cleanup_journal_invalid');
      const rows = state.rows.map(row => {
        if (!row || typeof row.identity !== 'string' || !integer(row.quantity) || row.quantity < 1) fail('cleanup_journal_invalid');
        return { identity: row.identity, quantity: row.quantity };
      }).sort((a, b) => a.identity.localeCompare(b.identity) || a.quantity - b.quantity);
      return encode({ ...state, rows });
    };
    // Shopify may rotate row keys after quantity/discount recalculation. Only
    // the verified poststate ignores keys; every identity, quantity and other
    // cart field stays exact, including when resuming an existing v1 journal.
    return withoutKeys(JSON.parse(coreFingerprint(cart))) === withoutKeys(expected);
  }
  function read(key) {
    try { return sessionStorage.getItem(key); }
    catch (_) { return null; }
  }
  function store(journal) {
    const body = JSON.stringify(journal);
    try {
      sessionStorage.setItem(JOURNAL_KEY, body);
      if (sessionStorage.getItem(JOURNAL_KEY) !== body) throw new Error('not_saved');
      memoryJournal = journal;
    } catch (_) { fail('storage_unavailable', 'Sepet doğrulama kaydı saklanamadı; satırlar değiştirilmedi.'); }
  }
  function clear() { memoryJournal = null; try { sessionStorage.removeItem(JOURNAL_KEY); } catch (_) {} }
  function studioJournal() {
    const raw = read(STUDIO_KEY);
    let journal = null;
    if (raw) {
      try { journal = JSON.parse(raw); } catch (_) { fail('studio_journal_invalid'); }
      if (journal?.version !== 2 || journal.scope !== 'studyom' || !validOperation(journal.operationId)) fail('studio_journal_invalid');
    }
    const recovery = window.FabrikoneStudioCartRecovery;
    const confirmed = recovery?.confirmedOperationId, active = recovery?.pendingOperationId;
    if (validOperation(confirmed)) {
      if (journal && journal.operationId !== confirmed) fail('different_studio_operation');
      return { version: 2, scope: 'studyom', operationId: confirmed, phase: 'cleanup' };
    }
    if (validOperation(active)) {
      if (journal && journal.operationId !== active) fail('different_studio_operation');
      return { version: 2, scope: 'studyom', operationId: active, phase: 'posted' };
    }
    return journal;
  }
  function pending() {
    const raw = read(JOURNAL_KEY);
    let journal = memoryJournal;
    if (raw) try { journal = JSON.parse(raw); } catch (_) { fail('cleanup_journal_invalid'); }
    if (!journal) return null;
    if (journal?.version !== 1 || journal.scope !== 'cart-lines' || !integer(journal.createdAt) || !integer(journal.attempts) || typeof journal.before !== 'string' || typeof journal.after !== 'string' || !Array.isArray(journal.updates) || !journal.updates.length || journal.updates.length > 500 || !integer(journal.groups) || journal.groups < 1 || journal.operationId && !validOperation(journal.operationId)) fail('cleanup_journal_invalid');
    const keys = new Set();
    for (const row of journal.updates) {
      if (!Array.isArray(row) || row.length !== 2 || typeof row[0] !== 'string' || !/^\d+:/.test(row[0]) || keys.has(row[0]) || !integer(row[1])) fail('cleanup_journal_invalid');
      keys.add(row[0]);
    }
    return journal;
  }
  const root = () => {
    const value = window.Shopify?.routes?.root || '/';
    return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value.endsWith('/') ? value : value + '/' : '/';
  };
  async function request(endpoint, body) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root() + endpoint, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, signal: controller.signal, ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}) });
      let cart;
      try { cart = await response.json(); } catch (_) { fail('response_unknown'); }
      if (!response.ok) fail('server_rejected', typeof cart?.description === 'string' ? cart.description : undefined);
      snapshot(cart);
      return cart;
    } catch (error) {
      if (error?.code === 'cart_lines_pending') throw error;
      fail('request_unknown');
    } finally { clearTimeout(timer); }
  }
  async function stableCart() {
    const first = await request('cart.js'), second = await request('cart.js');
    if (fingerprint(first) !== fingerprint(second)) fail('cart_changed');
    return second;
  }
  function authorize(options, active) {
    if (options.operationId) {
      if (!validOperation(options.operationId) || !active || active.operationId !== options.operationId || active.phase !== 'cleanup') fail('cleanup_proof_missing');
      return options.operationId;
    }
    return '';
  }
  function compatible(item, cart) {
    const p = prices(item, cart), global = orderDiscount(item, cart);
    if (!integer(p.original) || !integer(p.final) || p.original !== p.final || !integer(p.discounted) || !global && p.original !== p.discounted) return false;
    if (!Array.isArray(p.lineAllocations) || p.lineAllocations.length || Number(p.lineTotalDiscount || 0) !== 0) return false;
    if ([p.discounts, p.allocations].some(list => !Array.isArray(list) || !global && list.length)) return false;
    if (item.parent_relationship || item.item_components?.length || item.components?.length) return false;
    return true;
  }
  function build(cart, allowedOperation, active) {
    const groups = new Map();
    for (const item of cart.items) {
      if (!compatible(item, cart)) continue;
      const key = identity(item, true, cart, true);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    const updates = [], transfers = [];
    for (const group of groups.values()) {
      if (group.length < 2 || !group.some(operation)) continue;
      if (allowedOperation && !group.some(item => operation(item) === allowedOperation)) continue;
      if (active && group.some(item => operation(item) === active.operationId) && active.operationId !== allowedOperation) continue;
      const orderedGroup = [...group].sort((a, b) => a.key.localeCompare(b.key));
      const keeper = orderedGroup.find(item => !operation(item)) || orderedGroup[0];
      const quantity = group.reduce((total, item) => total + item.quantity, 0);
      if (!integer(quantity) || quantity < 1) fail('invalid_quantity');
      updates.push([keeper.key, quantity]);
      for (const item of orderedGroup) if (item !== keeper) updates.push([item.key, 0]);
      transfers.push({ keeper: keeper.key, quantity, removed: orderedGroup.filter(item => item !== keeper).map(item => item.key) });
    }
    if (!updates.length) return null;
    const before = snapshot(cart), core = snapshot(cart, true), quantities = new Map(updates);
    const expected = { ...core, rows: core.rows.map(row => ({ ...row, quantity: quantities.has(row.key) ? quantities.get(row.key) : row.quantity })).filter(row => row.quantity > 0) };
    return { version: 1, scope: 'cart-lines', createdAt: Date.now(), attempts: 0, operationId: allowedOperation, before: encode(before), after: encode(expected), updates, groups: transfers.length };
  }
  function result(cart, journal, changed) {
    return { cart, verified: true, changed, mergedGroups: journal?.groups || 0 };
  }
  async function transfer(journal, cart) {
    const current = fingerprint(cart);
    if (samePostState(cart, journal.after)) { clear(); return result(cart, journal, true); }
    if (current !== journal.before) fail('cart_changed');
    journal.attempts++;
    store(journal);
    // Both quantities are absolute and every key is from the exact fresh cart.
    // An unknown response is never followed by an additive request.
    await request('cart/update.js', { updates: Object.fromEntries(journal.updates) });
    const verified = await stableCart();
    if (!samePostState(verified, journal.after)) fail('transfer_unverified');
    clear();
    return result(verified, journal, true);
  }
  async function critical(options) {
    const active = studioJournal(), allowed = authorize(options, active), journal = pending();
    if (journal?.operationId && active?.operationId === journal.operationId && active.phase !== 'cleanup') fail('operation_protected');
    if (journal && allowed && journal.operationId && journal.operationId !== allowed) fail('different_cleanup_pending');
    const cart = await stableCart();
    if (journal) return transfer(journal, cart);
    if (options.cart && fingerprint(options.cart) !== fingerprint(cart)) fail('cart_changed');
    const next = build(cart, allowed, active);
    if (!next) return result(cart, null, false);
    store(next);
    return transfer(next, cart);
  }
  function coalesce(options = {}) {
    const run = async () => {
      if (navigator.locks?.request) return navigator.locks.request(LOCK, { mode: 'exclusive' }, () => critical(options));
      return critical(options);
    };
    const work = queue.then(run, run);
    queue = work.catch(() => {});
    return work;
  }
  window.FabrikoneCartLines = Object.freeze({ version: 1, coalesce });
})();
